const OP_MAP: Record<string, string> = {
  $gte: 'gte',
  $lte: 'lte',
  $lt: 'lt',
  $gt: 'gt',
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function toFullIso(value: unknown): unknown {
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) {
    return `${value}T00:00:00Z`
  }
  return value
}

/**
 * Normalise LLM-generated filter objects to the PISA dot-suffix format.
 *
 * Handles three common model mistakes:
 *   1. Underscore separator: { "beginAt_gte": "..." } → { "beginAt.gte": "..." }
 *   2. MongoDB nested:       { "beginAt": { "$gte": "..." } } → { "beginAt.gte": "..." }
 *   3. Date-only value:      "2026-04-27" → "2026-04-27T00:00:00Z"
 */
export function normalizeFilters(
  filters: Record<string, unknown>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(filters)) {
    // Case 2: value is a MongoDB operator object e.g. { $gte: "..." }
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      const obj = value as Record<string, unknown>
      const ops = Object.keys(obj).filter((k) => k in OP_MAP)
      if (ops.length > 0) {
        for (const op of ops) {
          result[`${key}.${OP_MAP[op]}`] = toFullIso(obj[op])
        }
        continue
      }
    }

    // Case 1: underscore separator e.g. "beginAt_gte"
    const underscoreMatch = /^(.+)_(gte|lte|lt|gt)$/.exec(key)
    if (underscoreMatch) {
      result[`${underscoreMatch[1]}.${underscoreMatch[2]}`] = toFullIso(value)
      continue
    }

    // Case 3: dot-suffix already correct but value is date-only
    const dotMatch = /^(.+)\.(gte|lte|lt|gt)$/.exec(key)
    if (dotMatch) {
      result[key] = toFullIso(value)
      continue
    }

    result[key] = value
  }

  return result
}
